import {
  Bar,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import { cn } from "../cn";
import { ChartEmptyState } from "./ChartEmptyState";
import type { ChartTimeSeriesPoint } from "./chartTypes";

export interface TrendBarChartProps {
  readonly data: readonly ChartTimeSeriesPoint[];
  /** Accessible name for the chart region, e.g. "Schadefoto's per dag". */
  readonly "aria-label": string;
  /** Formats an x-axis label (an ISO date by default) for the tooltip. */
  readonly formatLabel?: (label: string) => string;
  /** Formats the value for the tooltip, e.g. adding a unit. */
  readonly formatValue?: (value: number) => string;
  readonly className?: string;
  readonly height?: number;
}

function defaultFormatLabel(label: string): string {
  return label;
}

function defaultFormatValue(value: number): string {
  return value.toLocaleString("nl-NL");
}

function TrendTooltip({
  active,
  payload,
  formatLabel,
  formatValue,
}: {
  active?: boolean;
  payload?: readonly { value?: unknown; payload?: unknown }[];
  formatLabel: (label: string) => string;
  formatValue: (value: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0];
  const pointPayload = point.payload as ChartTimeSeriesPoint | undefined;
  const label = pointPayload?.label ?? "";

  return (
    <div className="rounded-lg border border-border bg-popover px-2.5 py-1.5 text-xs shadow-lg">
      <p className="font-medium text-popover-foreground">
        {formatValue(Number(point.value ?? 0))}
      </p>
      <p className="text-muted-foreground">{formatLabel(label)}</p>
    </div>
  );
}

/**
 * Time-series bar chart for daily/period trends (e.g. damage photos received
 * per day). Thin wrapper around Recharts rather than a hand-rolled SVG: axes,
 * tooltip positioning and resize handling are exactly the kind of boilerplate
 * a maintained chart library earns its keep on.
 */
export function TrendBarChart({
  data,
  "aria-label": ariaLabel,
  formatLabel = defaultFormatLabel,
  formatValue = defaultFormatValue,
  className,
  height = 200,
}: TrendBarChartProps) {
  if (data.length === 0) {
    return <ChartEmptyState className={className} height={height} />;
  }

  return (
    <div className={cn("relative w-full", className)} style={{ height }}>
      {/*
        The bars themselves carry no text, and the values only exist in the
        hover tooltip, so the chart is hidden from assistive technology and
        the same data is exposed as a screen-reader-only table instead.
      */}
      <div className="sr-only">
        <table>
          <caption>{ariaLabel}</caption>
          <tbody>
            {data.map((point) => (
              <tr key={point.label}>
                <th scope="row">{formatLabel(point.label)}</th>
                <td>{formatValue(point.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ResponsiveContainer width="100%" height="100%" aria-hidden="true">
        <RechartsBarChart
          data={data as ChartTimeSeriesPoint[]}
          margin={{ top: 4, right: 0, bottom: 0, left: 0 }}
        >
          <XAxis
            dataKey="label"
            tickFormatter={formatLabel}
            tick={{ fill: "var(--color-muted-foreground)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <Tooltip
            content={({ active, payload }) => (
              <TrendTooltip
                active={active}
                payload={payload}
                formatLabel={formatLabel}
                formatValue={formatValue}
              />
            )}
            cursor={{ fill: "var(--color-muted)" }}
          />
          <Bar
            dataKey="value"
            fill="var(--color-chart-1)"
            radius={[3, 3, 0, 0]}
            maxBarSize={28}
          />
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
}
